// per specs/060-agreed-alerts/plan.md C1, C4 (members), tasks T021
// The members of one watched group: added by medicine search (a commercial denomination, every
// code of it) or by DCI + dose + form (every producer), removed one at a time. Each act is its
// own write on the API and its own row of the group's history, so the list below always shows
// what the server holds and never a draft of it.
import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import {
  MudButton,
  MudIcon,
  MudInlineMessage,
  MudModal,
  MudSegmentedControl,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import {
  DialogCombobox,
  EmptyRegisterMessage,
  MetadataLine,
  onModalDismiss,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { formatMedicineLabel, medicineDisplayName } from '@simsm/shared-types';
import { apiErrorKey } from '../../app/api-client';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { fetchMedicinePage, type Medicine } from './medicines-api';
import { dciLine, sameTriple } from './watched-groups-domain';
import {
  addWatchedGroupMember,
  fetchDciOptions,
  removeWatchedGroupMember,
  WATCHED_GROUPS_KEY,
  type AddMemberBody,
  type DciOption,
  type WatchedGroup,
  type WatchedGroupMember,
} from './watched-groups-api';
import '../operator/reporting/stock-movement-dialog.css';
import './watched-groups.css';

// A query shorter than this is no search: the server treats one letter as the whole table.
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;

type AddMode = 'MEDICINE' | 'DCI_DOSE_FORM';

export function memberLabel(member: WatchedGroupMember): string {
  return member.kind === 'MEDICINE'
    ? member.medicineLabel || member.dci
    : dciLine({ dci: member.dci, dose: member.dose, form: member.form });
}

function invalidateGroup(queryClient: ReturnType<typeof useQueryClient>, groupId: string): void {
  void queryClient.invalidateQueries({ queryKey: [WATCHED_GROUPS_KEY] });
  void queryClient.invalidateQueries({ queryKey: ['watched-group-history', groupId] });
}

interface Props {
  group: WatchedGroup;
}

export function WatchedGroupMembers({ group }: Props): JSX.Element {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [removeErrorKey, setRemoveErrorKey] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (member: WatchedGroupMember) => removeWatchedGroupMember(group.id, member.id),
    onSuccess: (_, member) => {
      setRemoveErrorKey(null);
      invalidateGroup(queryClient, group.id);
      toast({
        variant: 'success',
        title: t('watched_groups.toast.member_removed'),
        text: memberLabel(member),
        closeLabel: t('common.close'),
      });
    },
    onError: (err: unknown) =>
      setRemoveErrorKey(apiErrorKey(err, 'watched_groups.error.member_remove_failed')),
  });

  async function askThenRemove(member: WatchedGroupMember): Promise<void> {
    const confirmed = await confirm({
      title: t('watched_groups.members.confirm_remove.title'),
      body: (
        <p>
          {t('watched_groups.members.confirm_remove.body', {
            member: memberLabel(member),
            group: group.name,
          })}
        </p>
      ),
      confirmLabel: t('watched_groups.members.action.remove'),
      cancelLabel: t('alerts.confirm.action.cancel'),
      destructive: true,
    });
    if (!confirmed) return;
    setRemoveErrorKey(null);
    remove.mutate(member);
  }

  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '76px' },
    { key: 'member', label: t('watched_groups.members.column.member') },
    { key: 'kind', label: t('watched_groups.members.column.kind'), width: '32%' },
  ];
  const rows: TableRowData[] = group.members.map((member) => ({ id: member.id }));

  return (
    <div className="simsm-watched-members">
      <div className="simsm-watched-members__toolbar">
        <span className="simsm-alerts__muted-text">
          {t(
            group.members.length === 1
              ? 'watched_groups.members.count_one'
              : 'watched_groups.members.count_other',
            { count: group.members.length },
          )}
        </span>
        <MudButton
          shape="circular"
          variant="strict"
          appearance="outlined"
          size="md"
          onClick={() => setAdding(true)}
        >
          <MudIcon slot="icon-start" name="plus-large" size={16} />
          {t('watched_groups.members.action.add')}
        </MudButton>
      </div>

      {removeErrorKey ? (
        <MudInlineMessage variant="error" size="small" role="alert">
          {t(removeErrorKey)}
        </MudInlineMessage>
      ) : null}

      <div className="simsm-alerts__register">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={rows}
          rowStyle="zebra"
          disableSort={true}
          aria-label={t('watched_groups.members.title')}
        >
          <EmptyRegisterMessage>{t('watched_groups.members.empty')}</EmptyRegisterMessage>
          <span slot="header-cell-actions" className="visually-hidden">
            {t('alerts.column.actions')}
          </span>
          {group.members.map((member, index) => [
            <span
              key={`actions-${member.id}`}
              slot={`cell-actions-${index}`}
              className="simsm-alerts__cell simsm-alerts__cell--actions"
            >
              <RowActionsMenu
                label={t('watched_groups.members.action.row_menu', { member: memberLabel(member) })}
                actions={[
                  {
                    label: t('watched_groups.members.action.remove'),
                    danger: true,
                    disabled: remove.isPending,
                    onSelect: () => {
                      void askThenRemove(member);
                    },
                  },
                ]}
              />
            </span>,
            <span
              key={`member-${member.id}`}
              slot={`cell-member-${index}`}
              className="simsm-alerts__cell"
            >
              {memberLabel(member)}
            </span>,
            <span
              key={`kind-${member.id}`}
              slot={`cell-kind-${index}`}
              className="simsm-alerts__cell"
            >
              <MudTag
                variant="info"
                size="sm"
                label={t(`watched_groups.members.kind.${member.kind}`)}
              />
            </span>,
          ])}
        </MudTable>
      </div>

      <AddMemberModal group={group} open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

interface AddMemberModalProps {
  group: WatchedGroup;
  open: boolean;
  onClose: () => void;
}

function AddMemberModal({ group, open, onClose }: AddMemberModalProps): JSX.Element {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<AddMode>('MEDICINE');
  const [query, setQuery] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [addedLabel, setAddedLabel] = useState<string | null>(null);

  const term = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const searching = open && term.length >= MIN_QUERY_LENGTH;

  const medicines = useQuery({
    queryKey: ['watched-group-medicine-search', term],
    queryFn: () =>
      fetchMedicinePage({
        q: term,
        kind: '',
        status: 'all',
        expiry: 'all',
        atcGroup: '',
        source: '',
        limit: SEARCH_LIMIT,
        offset: 0,
      }),
    enabled: searching && mode === 'MEDICINE',
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const triples = useQuery({
    queryKey: ['watched-group-dci-options', term],
    queryFn: () => fetchDciOptions(term),
    enabled: searching && mode === 'DCI_DOSE_FORM',
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const add = useMutation({
    mutationFn: ({ body }: { body: AddMemberBody; label: string }) =>
      addWatchedGroupMember(group.id, body),
    onSuccess: (_, { label }) => {
      setErrorKey(null);
      setQuery('');
      setAddedLabel(label);
      invalidateGroup(queryClient, group.id);
    },
    onError: (err: unknown) => {
      setAddedLabel(null);
      setErrorKey(apiErrorKey(err, 'watched_groups.error.member_add_failed'));
    },
  });

  const heldMedicineIds = useMemo(
    () => new Set(group.members.flatMap((m) => (m.medicineId ? [m.medicineId] : []))),
    [group.members],
  );

  const medicineOptions: Medicine[] = (medicines.data?.medicines ?? []).filter(
    (m) => !heldMedicineIds.has(m.id),
  );
  const tripleOptions: DciOption[] = (triples.data ?? []).filter(
    (option) => !group.members.some((m) => m.kind === 'DCI_DOSE_FORM' && sameTriple(m, option)),
  );

  const active = mode === 'MEDICINE' ? medicines : triples;
  const optionCount = mode === 'MEDICINE' ? medicineOptions.length : tripleOptions.length;

  let status: string | null = null;
  if (query.trim().length < MIN_QUERY_LENGTH) status = t('watched_groups.members.search.hint');
  else if (active.isFetching) status = t('watched_groups.members.search.loading');
  else if (active.isError) status = t('common.load_error');
  else if (optionCount === 0) status = t('watched_groups.members.search.empty', { query: term });

  function reset(): void {
    setQuery('');
    setErrorKey(null);
    setAddedLabel(null);
  }

  function close(): void {
    reset();
    onClose();
  }

  const listboxLabel = t('watched_groups.members.search.listbox');

  return (
    <MudModal
      closeLabel={t('common.close')}
      open={open}
      size="md"
      titleText={t('watched_groups.members.add_title', { group: group.name })}
      onMudClose={onModalDismiss(close)}
    >
      <div className="simsm-watched-add">
        <MudSegmentedControl
          size="sm"
          value={mode}
          aria-label={t('watched_groups.members.add_mode')}
          segments={[
            { value: 'MEDICINE', label: t('watched_groups.members.kind.MEDICINE') },
            { value: 'DCI_DOSE_FORM', label: t('watched_groups.members.kind.DCI_DOSE_FORM') },
          ]}
          onMudChange={(e: CustomEvent<{ value: string }>) => {
            setMode(e.detail.value as AddMode);
            reset();
          }}
        />
        <p className="simsm-watched-add__hint">
          {mode === 'MEDICINE'
            ? t('watched_groups.members.add_hint.MEDICINE')
            : t('watched_groups.members.add_hint.DCI_DOSE_FORM')}
        </p>

        {mode === 'MEDICINE' ? (
          <DialogCombobox<Medicine>
            className="simsm-watched-add__search"
            field="search"
            inline
            size="md"
            loading={active.isFetching}
            value={query}
            onValueChange={(value) => {
              setQuery(value);
              setAddedLabel(null);
            }}
            groups={[{ key: 'medicines', options: medicineOptions }]}
            optionKey={(medicine) => medicine.id}
            renderOption={(medicine) => (
              <span className="simsm-watched-add__option">
                <span className="simsm-watched-add__option-title">
                  {medicineDisplayName(medicine)}
                </span>
                <MetadataLine
                  wrap
                  items={[
                    { label: t('watched_groups.members.field.dci'), value: medicine.dci },
                    { label: t('watched_groups.members.field.dose'), value: medicine.dose },
                    {
                      label: t('watched_groups.members.field.form'),
                      value: medicine.pharmaceuticalForm,
                    },
                  ]}
                />
              </span>
            )}
            optionLabel={(medicine) => formatMedicineLabel(medicine)}
            onSelect={(medicine) =>
              add.mutate({
                body: { kind: 'MEDICINE', medicineId: medicine.id },
                label: medicineDisplayName(medicine),
              })
            }
            listboxLabel={listboxLabel}
            status={status}
            ariaLabel={t('watched_groups.members.search.medicine_label')}
            placeholder={t('watched_groups.members.search.medicine_label')}
          />
        ) : (
          <DialogCombobox<DciOption>
            className="simsm-watched-add__search"
            field="search"
            inline
            size="md"
            loading={active.isFetching}
            value={query}
            onValueChange={(value) => {
              setQuery(value);
              setAddedLabel(null);
            }}
            groups={[{ key: 'triples', options: tripleOptions }]}
            optionKey={(option) => `${option.dci}|${option.dose}|${option.form}`}
            renderOption={(option) => (
              <span className="simsm-watched-add__option">
                <span className="simsm-watched-add__option-title">{option.dci}</span>
                <MetadataLine
                  wrap
                  items={[
                    { label: t('watched_groups.members.field.dose'), value: option.dose },
                    { label: t('watched_groups.members.field.form'), value: option.form },
                  ]}
                />
              </span>
            )}
            optionLabel={(option) => dciLine(option)}
            onSelect={(option) =>
              add.mutate({
                body: {
                  kind: 'DCI_DOSE_FORM',
                  dci: option.dci,
                  dose: option.dose,
                  form: option.form,
                },
                label: dciLine(option),
              })
            }
            listboxLabel={listboxLabel}
            status={status}
            ariaLabel={t('watched_groups.members.search.dci_label')}
            placeholder={t('watched_groups.members.search.dci_label')}
          />
        )}

        {errorKey ? (
          <MudInlineMessage variant="error" size="small" role="alert">
            {t(errorKey)}
          </MudInlineMessage>
        ) : null}
        {addedLabel ? (
          <MudInlineMessage variant="success" size="small" role="status">
            {t('watched_groups.members.added', { member: addedLabel })}
          </MudInlineMessage>
        ) : null}
      </div>
      <div slot="actions" className="simsm-alerts__modal-actions">
        <MudButton shape="circular" variant="neutral" size="md" onClick={close}>
          {t('watched_groups.members.action.done')}
        </MudButton>
      </div>
    </MudModal>
  );
}
